import { DateTime } from 'luxon';
import createClient from '../create-client';
import { UnderlyingLastTradeResponse } from './types/UnderlyingLastTrade';

import {
  DateZone,
  MarketHours,
  NewsItem,
  UnderlyingHistory,
} from '@oai-frontend-monorepo/types';
import {
  decideLatestDateForHistory,
  symbolIsSPX,
} from '@oai-frontend-monorepo/utils';
import { EarningsResponse } from './types/EarningsResponse';

export class MarketApiClient {
  private client: ReturnType<typeof createClient>;

  constructor(getToken?: () => Promise<string | undefined>) {
    this.client = createClient(``, getToken);
  }

  public async getNewsBySymbol(symbol?: string) {
    if (!symbol) {
      throw new Error('Symbol is required');
    }

    return this.client.get<NewsItem[]>(`/news?symbols=${symbol}`);
  }

  public async getThisWeeksEarnings() {
    return this.client.get<EarningsResponse[]>('/earnings/this-week');
  }

  public async getNextWeeksEarnings() {
    return this.client.get<EarningsResponse[]>('/earnings/next-week');
  }

  public registerGetToken(
    baseUrl: string,
    getToken?: () => Promise<string | undefined>
  ) {
    this.client = createClient(baseUrl, getToken);

    return this;
  }

  async getUnderlyingLastTrade(symbol: string) {
    const usedSymbol = symbol === 'SPX' ? encodeURI('^SPX') : symbol;

    const response = await this.client.get<UnderlyingLastTradeResponse>(
      `/trades/closed-market?symbol=${usedSymbol}`
    );

    return response?.[0];
  }

  async getUnderlyingLastTrades(symbols: string[]) {
    const usedSymbols = symbols.map((s) =>
      s === 'SPX' ? encodeURI('^SPX') : s
    );

    const response = await this.client.get<UnderlyingLastTradeResponse>(
      `/trades/closed-market?symbol=${usedSymbols.join('&symbol=')}`
    );

    return response;
  }

  async getMarketHours() {
    const response = await this.client.get<MarketHours>('/market-status');

    return response;
  }

  async getUnderlyingHistory(
    symbol: string,
    date = DateTime.utc().setZone(DateZone.New_York).toFormat('yyyy-MM-dd'),
    unit = 'min',
    increment = 5
  ) {
    const usedSymbol = symbolIsSPX(symbol) ? '^SPX' : symbol;

    const response = await this.client.get<UnderlyingHistory[]>(
      `/historic/search/${encodeURIComponent(
        usedSymbol
      )}?increment=${increment}&unit=${unit}&stop=${decideLatestDateForHistory().toFormat(
        'yyyy-MM-dd'
      )}&start=${date}`
    );

    return response
      .map((p) => {
        const eowRegex = /^\d{4}-\d{2}-\d{2} 00:00:00$/;
        const isEow = eowRegex.test(p.date);

        if (isEow) {
          const returnValue = {
            ...p,
            date: `${DateTime.fromFormat(p.date, 'yyyy-MM-dd HH:mm:ss', {
              zone: DateZone.New_York,
            })
              .minus({ days: 3 })
              .plus({ hour: 16 })
              .toFormat('yyyy-MM-dd HH:mm:ss')}`,
          };

          return returnValue;
        }

        const regex = /^\d{4}-\d{2}-\d{2}$/;
        const isShortDate = regex.test(p.date);

        if (isShortDate) {
          return {
            ...p,
            date: `${p.date} 16:00:00`,
          };
        }

        return p;
      })
      .sort((a, b) => {
        let dateA = DateTime.fromFormat(a.date, 'yyyy-MM-dd HH:mm:ss', {
          zone: DateZone.New_York,
        });
        let dateB = DateTime.fromFormat(b.date, 'yyyy-MM-dd HH:mm:ss', {
          zone: DateZone.New_York,
        });

        if (dateA.isValid && dateB.isValid) {
          return dateA.toMillis() - dateB.toMillis();
        }

        dateA = DateTime.fromFormat(a.date, 'yyyy-MM-dd', {
          zone: DateZone.New_York,
        });

        dateB = DateTime.fromFormat(b.date, 'yyyy-MM-dd', {
          zone: DateZone.New_York,
        });

        return dateA.toMillis() - dateB.toMillis();
      })
      .filter((p) => {
        if (unit === 'day' || unit === 'week') {
          return p;
        }

        const dateA = DateTime.fromFormat(
          p.date,
          unit === 'min' || unit === 'hour'
            ? 'yyyy-MM-dd HH:mm:ss'
            : 'yyyy-MM-dd',
          {
            zone: DateZone.New_York,
          }
        );

        return (
          dateA.toMillis() <
            dateA.startOf('day').plus({ hour: 16 }).toMillis() &&
          dateA.toMillis() >=
            dateA.startOf('day').plus({ hour: 9, minute: 30 }).toMillis()
        );
      });
  }
}

export default new MarketApiClient();
